import { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import AppleIcon from '@mui/icons-material/Apple';
import EmailIcon from '@mui/icons-material/Email';
import GoogleIcon from '@mui/icons-material/Google';
import PersonIcon from '@mui/icons-material/Person';
import { Avatar, Box, Button, CircularProgress, Typography } from '@mui/material';
import { saveReturnToUrl } from '@utils/LocalStorageUtils';

import { useUIState } from '../../context/uiState';
import type { LastUserLogin } from '../../types/auth';

const getConnectionIcon = (connection?: string) => {
  switch (connection) {
    case 'google-oauth2':
      return <GoogleIcon sx={{ fontSize: 16 }} />;
    case 'apple':
      return <AppleIcon sx={{ fontSize: 16 }} />;
    case 'auth0':
      return <EmailIcon sx={{ fontSize: 16 }} />;
    default:
      return <PersonIcon sx={{ fontSize: 16 }} />;
  }
};

const getConnectionName = (connection?: string) => {
  switch (connection) {
    case 'google-oauth2':
      return 'Google';
    case 'apple':
      return 'Apple';
    case 'auth0':
      return 'Email/Password';
    default:
      return 'Account';
  }
};

interface SmartLoginButtonProps {
  lastUserLogin: LastUserLogin;
}

export const SmartLoginButton: React.FC<SmartLoginButtonProps> = ({ lastUserLogin }) => {
  const { loginWithRedirect, loginWithPopup } = useAuth0();
  const { state } = useUIState();
  const { loadedInChromeExtension } = state;
  const [isLoading, setIsLoading] = useState(false);

  const handleSmartLogin = async () => {
    setIsLoading(true);
    try {
      // Save the current URL so we can redirect back after login
      saveReturnToUrl();

      const authorizationParams: { login_hint?: string; connection?: string } = {};

      // Add login_hint for providers that support it
      // Note: Apple Sign In doesn't support login_hint parameter - it always shows its own account selector
      if (lastUserLogin.email && lastUserLogin.connection !== 'apple') {
        authorizationParams.login_hint = lastUserLogin.email;
      }

      // Direct to specific connection if known (but not for regular auth0)
      if (lastUserLogin.connection && lastUserLogin.connection !== 'auth0') {
        authorizationParams.connection = lastUserLogin.connection;
      }

      if (loadedInChromeExtension) {
        await loginWithPopup({
          authorizationParams,
        });
        window.location.href = '/callback';
      } else {
        await loginWithRedirect({
          authorizationParams,
        });
      }
    } catch (error) {
      console.error('Smart login failed:', error);
      setIsLoading(false);
    }
  };

  return (
    <Button
      variant="contained"
      size="large"
      onClick={handleSmartLogin}
      disabled={isLoading}
      sx={{
        py: { xs: 2.5, sm: 3 },
        px: { xs: 4, sm: 6 },
        minWidth: { xs: '100%', sm: '380px' },
        maxWidth: '480px',
        width: { xs: '100%', sm: 'auto' },
        textTransform: 'none',
        cursor: 'pointer',
        boxShadow: 'none',
        borderRadius: 2,
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 2,
        },
        transition: 'all 0.2s ease-in-out',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {isLoading ? (
          <CircularProgress size={20} sx={{ color: 'white' }} />
        ) : (
          <Avatar src={lastUserLogin.picture} sx={{ width: 40, height: 40 }}>
            {!lastUserLogin.picture && (lastUserLogin.name?.[0] || lastUserLogin.email?.[0])}
          </Avatar>
        )}
        <Box sx={{ textAlign: 'left' }}>
          <Typography variant="body1" sx={{ fontWeight: 500, color: 'white' }}>
            {isLoading
              ? 'Signing in...'
              : `Continue as ${lastUserLogin.name || lastUserLogin.email}`}
          </Typography>
          {!isLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {getConnectionIcon(lastUserLogin.connection)}
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                via {getConnectionName(lastUserLogin.connection)}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Button>
  );
};
